import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { authApi } from '../api/auth';

const MIN_PASSWORD_LENGTH = 12;

const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenStatus, setTokenStatus] = useState<{
    valid: boolean;
    email: string | null;
    full_name: string | null;
    expired: boolean;
    already_accepted: boolean;
    revoked: boolean;
    reason: string | null;
  } | null>(null);

  const tokenMissing = useMemo(() => token.length === 0, [token]);
  const passwordTooShort = useMemo(
    () => newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH,
    [newPassword],
  );

  // Validate the invitation token when component mounts
  useEffect(() => {
    if (tokenMissing) {
      setValidating(false);
      setError('Der Einladungslink ist ungueltig oder unvollstaendig.');
      return;
    }

    const validateToken = async () => {
      try {
        const status = await authApi.validateInvitation(token);
        setTokenStatus(status);
        
        if (!status.valid) {
          setError(status.reason || 'Der Einladungslink ist ungueltig.');
        }
      } catch (err: any) {
        setError('Fehler beim Ueberpruefen der Einladung. Bitte versuchen Sie es spaeter erneut.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token, tokenMissing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (tokenMissing) {
      setError('Der Einladungslink ist ungueltig oder unvollstaendig.');
      return;
    }

    if (!tokenStatus?.valid) {
      setError('Der Einladungslink ist nicht mehr gueltig.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Die Passwoerter stimmen nicht ueberein.');
      return;
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
      return;
    }

    setLoading(true);
    try {
      await authApi.acceptInvitation(token, newPassword);
      setDone(true);
      setInfo('Konto erfolgreich aktiviert. Sie koennen sich jetzt anmelden.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Einladung konnte nicht abgeschlossen werden.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Einladung annehmen</h2>
          <p className="mt-2 text-center text-sm text-gray-600">FeWo-Monitor</p>
        </div>

        {validating ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Einladung wird ueberprueft...</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
                {tokenStatus && (tokenStatus.expired || tokenStatus.already_accepted || tokenStatus.revoked) && (
                  <p className="mt-2 text-sm">
                    Bitte kontaktieren Sie den Administrator, um eine neue Einladung zu erhalten.
                  </p>
                )}
              </div>
            )}
            {info && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">{info}</div>
            )}

            {done ? (
              <div className="text-center">
                <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                  Zur Anmeldung
                </Link>
              </div>
            ) : tokenStatus?.valid ? (
              <>
                {tokenStatus.full_name && (
                  <div className="text-center text-gray-700">
                    <p className="text-sm">
                      Willkommen, <span className="font-semibold">{tokenStatus.full_name}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{tokenStatus.email}</p>
                  </div>
                )}
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="new-password" className="sr-only">
                      Neues Passwort
                    </label>
                    <input
                      id="new-password"
                      name="new-password"
                      type="password"
                      required
                      className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Neues Passwort"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Mindestens {MIN_PASSWORD_LENGTH} Zeichen.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="confirm-password" className="sr-only">
                      Passwort bestaetigen
                    </label>
                    <input
                      id="confirm-password"
                      name="confirm-password"
                      type="password"
                      required
                      className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Passwort bestaetigen"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading || passwordTooShort}
                      className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {loading ? 'Wird gespeichert...' : 'Konto aktivieren'}
                    </button>
                  </div>

                  <div className="text-center">
                    <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                      Zurueck zur Anmeldung
                    </Link>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center">
                <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                  Zur Anmeldung
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitation;
